import { safeFetch } from "../utils/safeFetch";
import i18n from "../i18n";

const ensureResponseOk = (response) => {
  if (response.ok) return;
  if (response.status === 0) {
    throw new Error("Impossibile connettersi al server");
  }
  throw new Error(`HTTP error! status: ${response.status}`);
};

export const getAllTavoli = async (token, tenant, options = {}) => {
  const noCache = Boolean(options?.noCache);
  const url = noCache ? `/api/tables?ts=${Date.now()}` : `/api/tables`;
  const response = await safeFetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'x-tenant-id': tenant
    }
  }, noCache ? null : 'allTables');

  if (!response.ok) {
    if (response.status === 0) {
      throw new Error("Impossibile connettersi al server o recuperare dati offline.");
    } else {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }

  return await response.json();
};

export const getTavoloById = async (tableId, token, tenant) => {
  const response = await safeFetch(`/api/tables/${tableId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'x-tenant-id': tenant
    }
  }, `table_${tableId}`);

  if (!response.ok) {
    if (response.status === 0) {
      throw new Error("Impossibile connettersi al server o recuperare dati offline.");
    } else {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }

  return await response.json();
};

export const creaTavolo = async (tavolo, token, tenant) => {
  if (tavolo.maxSeats >= 1) {
    const response = await safeFetch(`/api/tables`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        'x-tenant-id': tenant,
      },
      body: JSON.stringify(tavolo),
    });
    if (!response.ok) {
      if (response.status === 0) {
        throw new Error("Impossibile connettersi al server");
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    }

    return await response.json();
  } else { throw new Error("I posti non possono essere minori di 1!"); }
};

export const multiCreaTavolo = async (tavolo, token, tenant) => {
  const response = await safeFetch(`/api/tables/many`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      'x-tenant-id': tenant,
    },
    body: JSON.stringify(tavolo),
  });
  if (!response.ok) {
    if (response.status === 0) {
      throw new Error("Impossibile connettersi al server");
    } else {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }

  return await response.json();
};

export const aggiornaTavolo = async (tableId, updatedData, token, tenant) => {
  const response = await safeFetch(`/api/tables/${tableId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      'x-tenant-id': tenant,
    },
    body: JSON.stringify(updatedData),
  });

  if (!response.ok) {
    if (response.status === 0) {
      throw new Error("Impossibile connettersi al server");
    } else {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }

  return await response.json();
};

export const getCompatibleSetups = async (tableId, token, tenant) => {
  const response = await safeFetch(`/api/tables/${tableId}/compatible-setups`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
  });

  if (!response.ok) {
    if (response.status === 0) throw new Error("Impossibile connettersi al server");
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const updateCompatibleSetups = async (tableId, setupIds, token, tenant) => {
  const response = await safeFetch(`/api/tables/${tableId}/compatible-setups`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
    body: JSON.stringify({ setupIds }),
  });

  if (!response.ok) {
    if (response.status === 0) throw new Error("Impossibile connettersi al server");
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const eliminaTavolo = async (tableId, token, tenant) => {
  const response = await safeFetch(`/api/tables/${tableId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      'x-tenant-id': tenant,
    },
  });

  if (!response.ok) {
    if (response.status === 0) {
      throw new Error("Impossibile connettersi al server");
    } else {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }

  return "Tavolo eliminato";
};

const tableSessionHeaders = (token, tenant) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${token}`,
  "x-tenant-id": tenant,
});

const buildTableSessionError = async (response, fallbackMessage) => {
  let payload = null;
  try {
    payload = await response.json();
  } catch (_) {
    payload = null;
  }

  const message = response.status === 409
    ? i18n.t("tableSession.inUse")
    : (payload?.error || fallbackMessage || `HTTP error! status: ${response.status}`);
  const error = new Error(message);
  error.status = response.status;
  error.ownerEmail = payload?.ownerEmail || null;
  return error;
};

export const checkTableSession = async (tableId, token, tenant) => {
  const response = await safeFetch(`/api/tables/${tableId}/session?ts=${Date.now()}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
  });

  if (!response.ok) {
    throw await buildTableSessionError(response, "Impossibile verificare la sessione del tavolo");
  }

  return await response.json();
};

export const claimTableSession = async (tableId, token, tenant) => {
  const response = await safeFetch(`/api/tables/${tableId}/session`, {
    method: "POST",
    headers: tableSessionHeaders(token, tenant),
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    throw await buildTableSessionError(response, i18n.t("tableSession.inUse"));
  }

  return await response.json();
};

export const releaseTableSession = async (tableId, token, tenant, { keepalive = false } = {}) => {
  const response = await safeFetch(`/api/tables/${tableId}/session`, {
    method: "DELETE",
    headers: tableSessionHeaders(token, tenant),
    body: JSON.stringify({}),
    keepalive,
  });

  if (!response.ok) {
    throw await buildTableSessionError(response, "Impossibile liberare la sessione del tavolo");
  }

  return null;
};

// ========== COORDINATE X ==========

// Crea posX se non esiste (409 se già presente)
export const creaCoordinateX = async (tableId, value, token, tenant) => {
  const response = await safeFetch(`/api/tables/${tableId}/coordinate/x`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
    body: JSON.stringify({ value }),
  });

  ensureResponseOk(response);
  return await response.json();
};

// Legge posX (può essere null)
export const leggiCoordinateX = async (tableId, token, tenant) => {
  const response = await safeFetch(`/api/tables/${tableId}/coordinate/x`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
  });

  ensureResponseOk(response);
  return await response.json();
};

// Aggiorna/Imposta posX
export const aggiornaCoordinateX = async (tableId, value, token, tenant) => {
  const response = await safeFetch(`/api/tables/${tableId}/coordinate/x`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
    body: JSON.stringify({ value }),
  });

  ensureResponseOk(response);
  return await response.json();
};

// Azzera (null) posX
export const eliminaCoordinateX = async (tableId, token, tenant) => {
  const response = await safeFetch(`/api/tables/${tableId}/coordinate/x`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
  });

  ensureResponseOk(response);
  return await response.json();
};

// ========== COORDINATE Y ==========

// Crea posY se non esiste
export const creaCoordinateY = async (tableId, value, token, tenant) => {
  const response = await safeFetch(`/api/tables/${tableId}/coordinate/y`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
    body: JSON.stringify({ value }),
  });

  ensureResponseOk(response);
  return await response.json();
};

// Legge posY
export const leggiCoordinateY = async (tableId, token, tenant) => {
  const response = await safeFetch(`/api/tables/${tableId}/coordinate/y`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
  });

  ensureResponseOk(response);
  return await response.json();
};

// Aggiorna/Imposta posY
export const aggiornaCoordinateY = async (tableId, value, token, tenant) => {
  const response = await safeFetch(`/api/tables/${tableId}/coordinate/y`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
    body: JSON.stringify({ value }),
  });

  ensureResponseOk(response);
  return await response.json();
};

// Azzera (null) posY
export const eliminaCoordinateY = async (tableId, token, tenant) => {
  const response = await safeFetch(`/api/tables/${tableId}/coordinate/y`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
  });

  ensureResponseOk(response);
  return await response.json();
};
